/**
 * Statements editor — the intake for category 2.
 *
 * The company types account lines rather than uploading a PDF, because the
 * piece is billed by centimetre of column: its size has to be composed, not
 * received. Typing is also what lets the panel check the balance sheet balances
 * before anything is sent, which is cheaper for everyone than a correction
 * notice in the gazette.
 *
 * Values are typed in reais and kept as integer centavos. The running total of
 * each side is shown while typing, along with the difference — someone entering
 * forty lines should find out at line twelve that the sides diverged, not after
 * pressing send.
 */

import {
  ROTULOS_DEMONSTRACAO,
  TIPOS_DEMONSTRACAO,
  formatarCentavos,
  validarConjunto,
  type Demonstracao,
  type LinhaConta,
  type TipoDemonstracao,
} from '@egba-do/shared'
import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { api, type Item } from '../../api/cliente'
import { AreaTexto, Aviso, Botao, Campo, Cartao, Selecao } from '../ui'

/** "1.234,56" → 123456. Accepts what a person actually types. */
function paraCentavos(texto: string): number | null {
  const limpo = String(texto ?? '').trim()
  if (limpo === '') return null
  const negativo = /^\(.*\)$/.test(limpo) || limpo.startsWith('-')
  const n = Number.parseFloat(
    limpo.replace(/[()\-\s]/g, '').replace(/\./g, '').replace(',', '.'),
  )
  if (!Number.isFinite(n)) return null
  return Math.round(n * 100) * (negativo ? -1 : 1)
}

function paraTexto(centavos: number | null | undefined): string {
  if (centavos == null) return ''
  return formatarCentavos(centavos)
}

const SEM_LINHAS: TipoDemonstracao[] = ['notas_explicativas', 'parecer_auditoria']

export interface EditorDemonstracoesProps {
  pedidoId: string
  inicial: Demonstracao[]
  somenteLeitura?: boolean
  aoSalvar: () => void
  /** Live state for a side-by-side preview — fired on every edit, before save. */
  aoMudar?: (lista: Demonstracao[], colunas: number) => void
}

export function EditorDemonstracoes({
  pedidoId,
  inicial,
  somenteLeitura,
  aoSalvar,
  aoMudar,
}: EditorDemonstracoesProps) {
  const [lista, setListaBase] = useState<Demonstracao[]>(
    inicial.length > 0 ? inicial : [novaDemonstracao('balanco_patrimonial')],
  )
  const [colunas, setColunasBase] = useState(2)
  const setLista = (nova: Demonstracao[]) => {
    setListaBase(nova)
    aoMudar?.(nova, colunas)
  }
  const setColunas = (n: number) => {
    setColunasBase(n)
    aoMudar?.(lista, n)
  }
  const [erro, setErro] = useState<string | null>(null)
  const [estimativa, setEstimativa] = useState<{
    alturaCm: number
    colunas: number
    alturaPorColunaCm: number
  } | null>(null)

  const problemas = useMemo(() => validarConjunto(lista), [lista])
  const bloqueios = problemas.filter((p) => p.gravidade === 'rejeita')

  const salvar = useMutation({
    mutationFn: () =>
      api.put<Item<{ estimativa: { alturaCm: number; colunas: number; alturaPorColunaCm: number } }>>(
        `/api/v1/pedidos/${pedidoId}/demonstracoes`,
        { demonstracoes: lista, colunas },
      ),
    onSuccess: (r) => {
      setEstimativa(r.dados.estimativa)
      setErro(null)
      aoSalvar()
    },
    onError: (e: Error) => setErro(e.message),
  })

  function atualizar(i: number, mudanca: Partial<Demonstracao>): void {
    setLista(lista.map((d, j) => (j === i ? { ...d, ...mudanca } : d)))
  }

  function atualizarLinha(i: number, j: number, mudanca: Partial<LinhaConta>): void {
    atualizar(i, {
      linhas: lista[i]!.linhas.map((l, k) => (k === j ? { ...l, ...mudanca } : l)),
    })
  }

  return (
    <div className="space-y-6">
      <Cartao
        titulo="Demonstrações contábeis"
        descricao="Digitadas em linhas de conta — é o que permite compor a matéria em colunas e conferir se o balanço fecha antes de publicar."
        acoes={
          !somenteLeitura && (
            <div className="flex gap-2">
              <Selecao
                rotulo=""
                value={String(colunas)}
                onChange={(e) => setColunas(Number(e.target.value))}
                opcoes={[
                  { valor: '1', texto: '1 coluna' },
                  { valor: '2', texto: '2 colunas' },
                  { valor: '3', texto: '3 colunas' },
                ]}
              />
              <Botao
                carregando={salvar.isPending}
                disabled={bloqueios.length > 0}
                onClick={() => salvar.mutate()}
              >
                Salvar
              </Botao>
            </div>
          )
        }
      >
        {erro && <Aviso tom="perigo">{erro}</Aviso>}

        {bloqueios.length > 0 && (
          <Aviso tom="perigo" titulo="Precisa corrigir antes de enviar">
            <ul className="list-inside list-disc">
              {bloqueios.slice(0, 8).map((p, i) => (
                <li key={i}>
                  {ROTULOS_DEMONSTRACAO[p.demonstracao].sigla}
                  {p.linha ? ` · linha ${p.linha}` : ''}: {p.mensagem}
                </li>
              ))}
            </ul>
          </Aviso>
        )}

        {estimativa && bloqueios.length === 0 && (
          <Aviso tom="info" titulo="Estimativa de composição">
            Aproximadamente <strong>{estimativa.alturaCm} cm</strong> em {estimativa.colunas}{' '}
            coluna(s) — cerca de {estimativa.alturaPorColunaCm} cm por coluna. É uma{' '}
            <strong>estimativa</strong>: o valor cobrado sai da medição real da matéria depois de
            diagramada.
          </Aviso>
        )}
      </Cartao>

      {lista.map((d, i) => (
        <BlocoDemonstracao
          key={i}
          demonstracao={d}
          somenteLeitura={somenteLeitura}
          aoAlterar={(m) => atualizar(i, m)}
          aoAlterarLinha={(j, m) => atualizarLinha(i, j, m)}
          aoRemover={() => setLista(lista.filter((_, j) => j !== i))}
        />
      ))}

      {!somenteLeitura && (
        <div className="flex flex-wrap gap-2">
          {TIPOS_DEMONSTRACAO.filter((t) => !lista.some((d) => d.tipo === t)).map((t) => (
            <Botao
              key={t}
              variante="secundario"
              tamanho="sm"
              onClick={() => setLista([...lista, novaDemonstracao(t)])}
            >
              + {ROTULOS_DEMONSTRACAO[t].sigla}
            </Botao>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function novaDemonstracao(tipo: TipoDemonstracao): Demonstracao {
  const ano = new Date().getFullYear()
  if (SEM_LINHAS.includes(tipo)) {
    return { tipo, exercicio: ano, exercicioComparativo: ano - 1, texto: '', linhas: [] }
  }
  if (tipo === 'balanco_patrimonial') {
    // Nasce com o esqueleto do balanço: quem digita quarenta contas não deveria
    // ter de lembrar que existem dois lados e que ambos precisam de total.
    return {
      tipo,
      exercicio: ano,
      exercicioComparativo: ano - 1,
      linhas: [
        { descricao: 'Ativo Circulante', nivel: 1, valorCentavos: null, grupo: true, lado: 'ativo' },
        { descricao: 'Ativo Não Circulante', nivel: 1, valorCentavos: null, grupo: true, lado: 'ativo' },
        { descricao: 'Total do Ativo', nivel: 0, valorCentavos: null, grupo: true, lado: 'ativo' },
        { descricao: 'Passivo Circulante', nivel: 1, valorCentavos: null, grupo: true, lado: 'passivo' },
        { descricao: 'Passivo Não Circulante', nivel: 1, valorCentavos: null, grupo: true, lado: 'passivo' },
        { descricao: 'Patrimônio Líquido', nivel: 1, valorCentavos: null, grupo: true, lado: 'passivo' },
        {
          descricao: 'Total do Passivo e Patrimônio Líquido',
          nivel: 0,
          valorCentavos: null,
          grupo: true,
          lado: 'passivo',
        },
      ],
    }
  }
  return {
    tipo,
    exercicio: ano,
    exercicioComparativo: ano - 1,
    linhas: [{ descricao: '', nivel: 0, valorCentavos: null }],
  }
}

/** Sum of the shallowest `grupo` lines on one side — the same rule the validator uses. */
function totalDoLado(linhas: readonly LinhaConta[], lado: 'ativo' | 'passivo'): number | null {
  const doLado = linhas.filter((l) => l.lado === lado && l.grupo)
  if (doLado.length === 0) return null
  const nivelMinimo = Math.min(...doLado.map((l) => l.nivel))
  return doLado
    .filter((l) => l.nivel === nivelMinimo)
    .reduce((s, l) => s + (l.valorCentavos ?? 0), 0)
}

function BlocoDemonstracao({
  demonstracao: d,
  somenteLeitura,
  aoAlterar,
  aoAlterarLinha,
  aoRemover,
}: {
  demonstracao: Demonstracao
  somenteLeitura?: boolean
  aoAlterar: (m: Partial<Demonstracao>) => void
  aoAlterarLinha: (j: number, m: Partial<LinhaConta>) => void
  aoRemover: () => void
}) {
  const rotulo = ROTULOS_DEMONSTRACAO[d.tipo]
  const ehBalanco = d.tipo === 'balanco_patrimonial'
  const somenteTexto = SEM_LINHAS.includes(d.tipo)

  const ativo = ehBalanco ? totalDoLado(d.linhas, 'ativo') : null
  const passivo = ehBalanco ? totalDoLado(d.linhas, 'passivo') : null
  const diferenca = ativo != null && passivo != null ? ativo - passivo : null

  return (
    <Cartao
      titulo={rotulo.nome}
      acoes={
        !somenteLeitura && (
          <Botao variante="secundario" tamanho="sm" onClick={aoRemover}>
            Remover
          </Botao>
        )
      }
    >
      <div className="mb-5 grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="Exercício"
          type="number"
          disabled={somenteLeitura}
          value={String(d.exercicio)}
          onChange={(e) => aoAlterar({ exercicio: Number(e.target.value) })}
        />
        <Campo
          rotulo="Exercício comparativo"
          type="number"
          disabled={somenteLeitura}
          value={d.exercicioComparativo != null ? String(d.exercicioComparativo) : ''}
          onChange={(e) =>
            aoAlterar({ exercicioComparativo: e.target.value ? Number(e.target.value) : null })
          }
          ajuda="Publicado ao lado, na mesma tabela."
        />
      </div>

      {somenteTexto ? (
        <AreaTexto
          rotulo="Texto"
          rows={10}
          disabled={somenteLeitura}
          value={d.texto ?? ''}
          onChange={(e) => aoAlterar({ texto: e.target.value })}
          ajuda="Texto corrido, publicado como está."
        />
      ) : (
        <>
          {/* Conferência ao vivo: quem digita quarenta linhas precisa descobrir
              na décima segunda que os lados divergiram, não no envio. */}
          {ehBalanco && (
            <div
              className={`mb-4 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg px-4 py-3 text-sm ${
                diferenca === 0
                  ? 'bg-[color:var(--color-sucesso-fundo)] text-emerald-900'
                  : 'bg-[color:var(--color-atencao-fundo)] text-amber-900'
              }`}
            >
              <span>
                Ativo: <strong className="tabular">{formatarCentavos(ativo)}</strong>
              </span>
              <span>
                Passivo + PL: <strong className="tabular">{formatarCentavos(passivo)}</strong>
              </span>
              <span>
                {diferenca === 0 ? (
                  'fecha'
                ) : (
                  <>
                    diferença de{' '}
                    <strong className="tabular">{formatarCentavos(Math.abs(diferenca ?? 0))}</strong>
                  </>
                )}
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            {d.linhas.map((l, j) => (
              // A descrição manda no espaço: com o editor lado a lado com a
              // prévia, valores e botões quebram para a linha de baixo em vez
              // de espremer a conta até virar "Ativ…".
              <div key={j} className="flex flex-wrap items-center gap-2">
                <input
                  value={l.descricao}
                  disabled={somenteLeitura}
                  onChange={(e) => aoAlterarLinha(j, { descricao: e.target.value })}
                  placeholder="Conta"
                  style={{ paddingLeft: `${12 + l.nivel * 16}px` }}
                  className={`campo min-w-56 flex-[3] ${l.grupo ? 'font-semibold' : ''}`}
                />
                <div className="flex flex-1 items-center justify-end gap-2">
                <input
                  value={paraTexto(l.valorCentavos)}
                  disabled={somenteLeitura}
                  onChange={(e) => aoAlterarLinha(j, { valorCentavos: paraCentavos(e.target.value) })}
                  placeholder="0,00"
                  inputMode="decimal"
                  className="campo w-32 text-right tabular"
                />
                {d.exercicioComparativo != null && (
                  <input
                    value={paraTexto(l.comparativoCentavos)}
                    disabled={somenteLeitura}
                    onChange={(e) =>
                      aoAlterarLinha(j, { comparativoCentavos: paraCentavos(e.target.value) })
                    }
                    placeholder="0,00"
                    inputMode="decimal"
                    className="campo w-32 text-right tabular text-slate-500"
                  />
                )}
                {!somenteLeitura && (
                  <div className="flex shrink-0 gap-0.5">
                    <BotaoMini
                      titulo="Subir linha"
                      onClick={() => {
                        if (j === 0) return
                        const novas = [...d.linhas]
                        ;[novas[j - 1], novas[j]] = [novas[j]!, novas[j - 1]!]
                        aoAlterar({ linhas: novas })
                      }}
                    >
                      ↑
                    </BotaoMini>
                    <BotaoMini
                      titulo="Descer linha"
                      onClick={() => {
                        if (j === d.linhas.length - 1) return
                        const novas = [...d.linhas]
                        ;[novas[j], novas[j + 1]] = [novas[j + 1]!, novas[j]!]
                        aoAlterar({ linhas: novas })
                      }}
                    >
                      ↓
                    </BotaoMini>
                    <BotaoMini
                      titulo="Recuar"
                      onClick={() => aoAlterarLinha(j, { nivel: Math.max(0, l.nivel - 1) })}
                    >
                      ←
                    </BotaoMini>
                    <BotaoMini
                      titulo="Avançar"
                      onClick={() => aoAlterarLinha(j, { nivel: Math.min(5, l.nivel + 1) })}
                    >
                      →
                    </BotaoMini>
                    <BotaoMini
                      titulo={l.grupo ? 'Deixar de ser total' : 'Marcar como total'}
                      ativo={l.grupo}
                      onClick={() => aoAlterarLinha(j, { grupo: !l.grupo })}
                    >
                      Σ
                    </BotaoMini>
                    {ehBalanco && (
                      <BotaoMini
                        titulo={l.lado === 'ativo' ? 'Passar para o passivo' : 'Passar para o ativo'}
                        onClick={() =>
                          aoAlterarLinha(j, { lado: l.lado === 'ativo' ? 'passivo' : 'ativo' })
                        }
                      >
                        {l.lado === 'ativo' ? 'A' : 'P'}
                      </BotaoMini>
                    )}
                    <BotaoMini
                      titulo="Remover linha"
                      onClick={() =>
                        aoAlterar({ linhas: d.linhas.filter((_, k) => k !== j) })
                      }
                    >
                      ×
                    </BotaoMini>
                  </div>
                )}
                </div>
              </div>
            ))}
          </div>

          {!somenteLeitura && (
            <div className="mt-3">
              <Botao
                variante="secundario"
                tamanho="sm"
                onClick={() =>
                  aoAlterar({
                    linhas: [
                      ...d.linhas,
                      {
                        descricao: '',
                        nivel: d.linhas.at(-1)?.nivel ?? 0,
                        valorCentavos: null,
                        lado: ehBalanco ? (d.linhas.at(-1)?.lado ?? 'ativo') : undefined,
                      },
                    ],
                  })
                }
              >
                + Linha
              </Botao>
            </div>
          )}
        </>
      )}
    </Cartao>
  )
}

function BotaoMini({
  children,
  titulo,
  ativo,
  onClick,
}: {
  children: React.ReactNode
  titulo: string
  ativo?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      onClick={onClick}
      className={`flex size-7 items-center justify-center rounded text-xs transition ${
        ativo
          ? 'bg-marca-100 text-marca-800'
          : 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
      }`}
    >
      {children}
    </button>
  )
}
